import React, { useCallback, useMemo, useRef } from 'react';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import { Box } from '@material-ui/core';

import { ProjectAllowedReposNotice } from './components/ProjectAllowedReposNotice';
import { BasicInfoSection } from './sections/BasicInfoSection';
import { ClusterSection } from './sections/ClusterSection';
import { EditBasicInfoSection } from './sections/EditBasicInfoSection';
import { NotificationSection } from './sections/NotificationSection';
import { OwnerSection } from './sections/OwnerSection';
import { useStyles } from './styles';
import {
  ClusterConfiguration,
  DaasProjectInputData,
  DEFAULT_DATA,
  NotificationService,
  ProjectBasicInfoValue,
  ProjectNotificationValue,
  ProjectOwnerValue,
} from './types';

export interface DaasProjectInputUiOptions {
  fillEditFormData?: boolean;
}

export const DaasProjectInput = (
  props: FieldExtensionComponentProps<
    DaasProjectInputData,
    DaasProjectInputUiOptions
  >,
) => {
  const { onChange, formData, uiSchema, rawErrors } = props;
  const fillEditFormData = useMemo(
    () => uiSchema?.['ui:options']?.fillEditFormData as boolean | undefined,
    [uiSchema],
  );

  const classes = useStyles();
  const data = formData ?? DEFAULT_DATA;

  // Stabilize rawErrors — Backstage creates a new array reference on every render
  // even when contents are unchanged (typically []). Without this, React.memo on
  // sections is defeated because every section receives a new errors prop each render.
  const stableErrorsRef = useRef<string[]>(rawErrors ?? []);
  const nextErrors = rawErrors ?? [];
  if (
    nextErrors.length !== stableErrorsRef.current.length ||
    nextErrors.some((e, i) => e !== stableErrorsRef.current[i])
  ) {
    stableErrorsRef.current = nextErrors;
  }
  const errors = stableErrorsRef.current;

  // Refs to latest values — allow stable callbacks without stale closures
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const dataRef = useRef(data);
  dataRef.current = data;

  // Stable callbacks
  const handleBasicInfoChange = useCallback((v: ProjectBasicInfoValue) => {
    onChangeRef.current({ ...dataRef.current, ...v });
  }, []);

  const handleProjectEntitySelect = useCallback(
    (fullData: DaasProjectInputData) => {
      onChangeRef.current({
        ...dataRef.current,
        ...fullData,
        _initialized: true,
      });
    },
    [],
  );

  const handleOwnerChange = useCallback((v: ProjectOwnerValue) => {
    onChangeRef.current({ ...dataRef.current, ...v });
  }, []);

  const handleClustersChange = useCallback((v: ClusterConfiguration) => {
    onChangeRef.current({ ...dataRef.current, clusters: v });
  }, []);

  const handleNotificationChange = useCallback(
    (v: ProjectNotificationValue) => {
      onChangeRef.current({ ...dataRef.current, ...v });
    },
    [],
  );

  // Memoized value slices — sections only re-render when their own data changes
  const basicInfoValue = useMemo(
    () => ({
      projectName: data.projectName ?? '',
      description: data.description ?? '',
      projectRef: data.projectRef,
    }),
    [data.projectName, data.description, data.projectRef],
  );

  const ownerValue = useMemo(
    () => ({
      owner: data.owner,
      securityGroups: data.securityGroups,
      cicdOidcSubjects: data.cicdOidcSubjects ?? [],
    }),
    [data.owner, data.securityGroups, data.cicdOidcSubjects],
  );

  const notificationValue = useMemo(
    () => ({
      notificationChannel: data.notificationChannel,
      notificationServices: data.notificationServices as NotificationService[],
    }),
    [data.notificationChannel, data.notificationServices],
  );

  return (
    <Box className={classes.root}>
      <Box className={classes.mainContainer}>
        {fillEditFormData ? (
          <EditBasicInfoSection
            value={basicInfoValue}
            onChange={handleBasicInfoChange}
            onProjectEntitySelect={handleProjectEntitySelect}
            isFormAlreadyPopulated={!!data._initialized}
            errors={errors}
          />
        ) : (
          <BasicInfoSection
            value={basicInfoValue}
            onChange={handleBasicInfoChange}
            errors={errors}
          />
        )}
        <OwnerSection
          value={ownerValue}
          onChange={handleOwnerChange}
          errors={errors}
        />
        <ClusterSection
          value={data.clusters}
          onChange={handleClustersChange}
          errors={errors}
        />
        <ProjectAllowedReposNotice />
        <NotificationSection
          value={notificationValue}
          onChange={handleNotificationChange}
          errors={errors}
        />
      </Box>
    </Box>
  );
};
